import { GeneoIotProjectFile } from '@geneo2-web/shared-ui';
import { GetProjectResourcesByChapterData } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface ISelection {
  id: number;
  name: string;
}

interface ISectionSelection {
  id: bigint;
  name: string;
}
/** Marks a project starts at, and the step the +/- controls move it by. */
export const DEFAULT_PROJECT_MARKS = 25;
export const PROJECT_MARKS_STEP = 5;
/**
 * The backend stores the name as unbounded text, so this cap is ours. Chapter
 * and robotics project titles run up to ~60 characters; 100 leaves room for a
 * teacher's suffix (batch, term) and matches the group-name limit.
 */
export const MAX_PROJECT_NAME_LENGTH = 100;

const defaultInstructions = [
  'Complete the project within the given timeline.',
  'Ensure all team members contribute equally to the project.',
  'Maintain originality and avoid plagiarism.',
  'Test your project thoroughly before submission.',
  'Handle all robotic components, sensors, and equipment with care.',
];
/**
 * A picked resource, pinned to the lesson plan it was picked from - the same
 * resourceId can sit in two lesson plans, and creation needs to know which one.
 * The array order is the order the student progresses through them, so it maps
 * 1-based onto `selectedResourcesInfo[].sequence` at creation.
 */
export interface ISelectedProjectResource {
  resourceId: string;
  lessonId: string;
  moduleId: number;
}

/**
 * A collaboration group as the teacher built it. `studentIds` are int64s kept as
 * strings - that is how the API carries them in JSON, and it keeps the store
 * plainly serializable. `studentGroupId` is absent until the assign call
 * returns the server-assigned id.
 */
export interface IProjectStudentGroup {
  clientGroupId: string;
  groupName: string;
  sectionId: bigint;
  studentIds: string[];
  studentGroupId?: bigint;
}

export interface IProjectFlowState {
  project_resource_data?: GetProjectResourcesByChapterData;
  /** A project spans exactly one chapter. */
  selected_chapter_id?: number;
  selected_resources: ISelectedProjectResource[];

  selected_class?: ISelection;
  selected_section?: ISectionSelection;
  selected_subject?: ISelection;
  selected_project_id?: bigint;
  project_name: string;
  project_instructions: string[];
  /** Total marks the project is graded out of. Stepped in multiples of 5. */
  project_marks: number;
  /**
   * The teacher's own worked solution, as the editor emitted it on the Settings
   * step. Kept here so stepping back to resource selection does not lose it, and
   * so creation can put it on the practical resource's `metaData`.
   */
  teacher_solution?: GeneoIotProjectFile;

  /** Set by assessmentCreationSubmit; the assign call needs it. */
  created_assessment_id?: bigint;
  /** Groups echoed back by the assign call, carrying their real ids. */
  assigned_student_groups: IProjectStudentGroup[];
}

const initialState: IProjectFlowState = {
  project_resource_data: undefined,
  selected_chapter_id: undefined,
  selected_resources: [],

  selected_class: undefined,
  selected_section: undefined,
  selected_subject: undefined,
  selected_project_id: undefined,
  project_name: '',
  project_instructions: defaultInstructions,
  project_marks: DEFAULT_PROJECT_MARKS,
  teacher_solution: undefined,
  created_assessment_id: undefined,
  assigned_student_groups: [],
};

export const projectFlowSlice = createSlice({
  name: 'projectFlow',
  initialState,
  reducers: {
    setProjectResourceData: (
      state,
      action: PayloadAction<GetProjectResourcesByChapterData | undefined>
    ) => {
      state.project_resource_data = action.payload;
    },

    setProjectSelection: (
      state,
      action: PayloadAction<{
        chapterId?: number;
        resources: ISelectedProjectResource[];
      }>
    ) => {
      state.selected_chapter_id = action.payload.resources.length
        ? action.payload.chapterId
        : undefined;
      state.selected_resources = action.payload.resources;
    },

    clearProjectSelection: (state) => {
      state.selected_chapter_id = undefined;
      state.selected_resources = [];
    },

    setSelectedClass: (
      state,
      action: PayloadAction<ISelection | undefined>
    ) => {
      state.selected_class = action.payload;
    },

    setSelectedSection: (
      state,
      action: PayloadAction<ISectionSelection | undefined>
    ) => {
      state.selected_section = action.payload;
    },

    setSelectedSubject: (
      state,
      action: PayloadAction<ISelection | undefined>
    ) => {
      state.selected_subject = action.payload;
    },

    setProjectName: (state, action: PayloadAction<string>) => {
      state.project_name = action.payload;
    },
    setSelectedProjectId: (
      state,
      action: PayloadAction<bigint | undefined>
    ) => {
      state.selected_project_id = action.payload;
    },

    setProjectInstructions: (state, action: PayloadAction<string[]>) => {
      state.project_instructions = action.payload;
    },

    setProjectMarks: (state, action: PayloadAction<number>) => {
      state.project_marks = action.payload;
    },

    setTeacherSolution: (
      state,
      action: PayloadAction<GeneoIotProjectFile | undefined>
    ) => {
      state.teacher_solution = action.payload;
    },

    setCreatedAssessmentId: (
      state,
      action: PayloadAction<bigint | undefined>
    ) => {
      state.created_assessment_id = action.payload;
    },

    setAssignedStudentGroups: (
      state,
      action: PayloadAction<IProjectStudentGroup[]>
    ) => {
      state.assigned_student_groups = action.payload;
    },

    resetProjectFlow: (state) => {
      state.selected_class = undefined;
      state.selected_section = undefined;
      state.selected_subject = undefined;
      state.selected_project_id = undefined;
      state.project_name = '';
      state.project_instructions = [...defaultInstructions];
      state.project_marks = DEFAULT_PROJECT_MARKS;
      state.teacher_solution = undefined;
      state.project_resource_data = undefined;
      state.selected_chapter_id = undefined;
      state.selected_resources = [];
      state.created_assessment_id = undefined;
      state.assigned_student_groups = [];
    },
  },
});

export const {
  setProjectResourceData,
  setProjectSelection,
  clearProjectSelection,
  setSelectedClass,
  setSelectedSection,
  setSelectedSubject,
  setSelectedProjectId,
  setProjectName,
  setProjectInstructions,
  setProjectMarks,
  setTeacherSolution,
  setCreatedAssessmentId,
  setAssignedStudentGroups,
  resetProjectFlow,
} = projectFlowSlice.actions;

export default projectFlowSlice.reducer;
